import type {
	CreateDiningHallBody,
	CreateTakeawayOrderBody,
	DiningHall,
	DiningHallBuildingOption,
	DiningHallOption,
	DiningHallsPage,
	FnbNocOverview,
	FnbSettings,
	ListMealScansQueryInput,
	ListTakeawayOrdersQueryInput,
	MealPlan,
	MealScanBody,
	MealScanResult,
	MealScansPage,
	PatchDiningHallBody,
	PutFnbSettingsBody,
	TakeawayBulkStatusBody,
	TakeawayBulkStatusResult,
	TakeawayOrderItem,
	TakeawayOrderNocItem,
	TakeawayOrdersPage,
	TakeawayPageCounts,
	TakeawayOrderView,
	TakeawayStatusBody,
} from "@seagames/contracts";
import { PAGE_SIZE_MAX } from "@seagames/contracts";
import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import { api } from "./client";
import { queryClient } from "./query-client";

/**
 * Food & Beverages (M12): the host desk's, the kitchen door's and the NOC's
 * reads and writes, on the contract's wire shapes (`@seagames/contracts`
 * fnb.ts). Every read is ONE request the API answers with one statement;
 * nothing here loops over rows. Keys live under `["fnb", …]`, so one
 * invalidate refreshes every F&B view.
 */

/** Drop unset keys so equal filters make equal query keys. */
const clean = <T extends object>(o: T): T => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== "")) as T;

/** `/takeaway-orders` filters in WIRE form (`status` is the comma list, `late` is "true"). */
export type TakeawayOrdersFilter = Omit<ListTakeawayOrdersQueryInput, "status" | "late"> & { status?: string; late?: "true" };

export const fnbKeys = {
	all: ["fnb"] as const,
	settings: (gamesId: string) => ["fnb", "settings", gamesId] as const,
	halls: (gamesId: string) => ["fnb", "halls", gamesId] as const,
	hallOptions: (gamesId: string) => ["fnb", "hall-options", gamesId] as const,
	buildingOptions: (gamesId: string) => ["fnb", "building-options", gamesId] as const,
	mealPlan: (gamesId: string, date: string | undefined) => ["fnb", "meal-plan", gamesId, date ?? null] as const,
	noc: (gamesId: string, date: string | undefined) => ["fnb", "noc", gamesId, date ?? null] as const,
	scans: (f: ListMealScansQueryInput) => ["fnb", "scans", clean(f)] as const,
	orders: (f: TakeawayOrdersFilter) => ["fnb", "orders", clean(f)] as const,
};

/** `GET /fnb/settings` — a games with no row reads the defaults (`updated_at` null). */
export const fnbSettingsOptions = (gamesId: string, enabled = true) =>
	queryOptions({
		queryKey: fnbKeys.settings(gamesId),
		queryFn: ({ signal }) => api.get<FnbSettings>("/fnb/settings", { query: { games_id: gamesId }, signal }),
		staleTime: 60_000,
		enabled,
	});

/** `GET /dining-halls` — every hall of the games (inactive too), one page of PAGE_SIZE_MAX: a small list. */
export const diningHallsOptions = (gamesId: string, enabled = true) =>
	queryOptions({
		queryKey: fnbKeys.halls(gamesId),
		queryFn: ({ signal }) => api.get<DiningHallsPage>("/dining-halls", { query: { games_id: gamesId, page_size: PAGE_SIZE_MAX }, signal }),
		staleTime: 30_000,
		enabled,
	});

/** `GET /dining-halls/options` — ACTIVE halls (the scan station's picker, the order form). */
export const diningHallOptionsOptions = (gamesId: string, enabled = true) =>
	queryOptions({
		queryKey: fnbKeys.hallOptions(gamesId),
		queryFn: async ({ signal }) => (await api.get<{ items: DiningHallOption[] }>("/dining-halls/options", { query: { games_id: gamesId }, signal })).items,
		staleTime: 60_000,
		enabled,
	});

/** `GET /dining-halls/building-options` — lodging buildings with their cluster and the hall serving each (the hall form). Admins and `fnb.manage`. */
export const diningHallBuildingOptionsOptions = (gamesId: string, enabled = true) =>
	queryOptions({
		queryKey: fnbKeys.buildingOptions(gamesId),
		queryFn: async ({ signal }) =>
			(await api.get<{ items: DiningHallBuildingOption[] }>("/dining-halls/building-options", { query: { games_id: gamesId }, signal })).items,
		staleTime: 30_000,
		enabled,
	});

/** `GET /fnb/meal-plan` — the host Overview: halls × meals, the strip and Needs attention, one statement. `date` omitted = today (games' zone). */
export const mealPlanOptions = (gamesId: string, date?: string, enabled = true) =>
	queryOptions({
		queryKey: fnbKeys.mealPlan(gamesId, date),
		queryFn: ({ signal }) => api.get<MealPlan>("/fnb/meal-plan", { query: clean({ games_id: gamesId, date }), signal }),
		placeholderData: keepPreviousData,
		staleTime: 30_000,
		enabled,
	});

/** `GET /fnb/noc` — a NOC's own halls and its own orders per status. Never another organisation's numbers. */
export const fnbNocOptions = (gamesId: string, date?: string) =>
	queryOptions({
		queryKey: fnbKeys.noc(gamesId, date),
		queryFn: ({ signal }) => api.get<FnbNocOverview>("/fnb/noc", { query: clean({ games_id: gamesId, date }), signal }),
		staleTime: 30_000,
	});

/** `GET /meal-scans` — keyset, one service day (today by default); the total rides on the first page. */
export const mealScansOptions = (f: ListMealScansQueryInput, enabled = true) =>
	queryOptions({
		queryKey: fnbKeys.scans(f),
		queryFn: ({ signal }) => api.get<MealScansPage>("/meal-scans", { query: { ...clean(f) } as Record<string, string | number>, signal }),
		staleTime: 10_000,
		enabled,
	});

/** One order row, either view: a NOC's has no host user on it (`created_by` / `decided_by` absent). */
export type TakeawayOrderRow = TakeawayOrderNocItem & Partial<Pick<TakeawayOrderItem, "created_by" | "decided_by">>;
/** `TakeawayOrdersPage` with its two row shapes folded into one (the view still says which). */
export type TakeawayOrdersList = { items: TakeawayOrderRow[]; next_cursor: string | null; total: number | null } & Partial<
	{ view: TakeawayOrdersPage["view"]; has_more: boolean } & Omit<TakeawayPageCounts, "total">
>;

/** `GET /takeaway-orders` — keyset; total, status counts and the late count ride on the first page. The row shape follows the caller (`view`). */
export const takeawayOrdersOptions = (f: TakeawayOrdersFilter) =>
	queryOptions({
		queryKey: fnbKeys.orders(f),
		queryFn: async ({ signal }): Promise<TakeawayOrdersList> =>
			api.get<TakeawayOrdersPage>("/takeaway-orders", { query: { ...clean(f) } as Record<string, string | number>, signal }),
		placeholderData: keepPreviousData,
		staleTime: 15_000,
	});

/** After any F&B write: every F&B read (plan, halls, orders, scans, the NOC page). */
export const invalidateFnb = () => queryClient.invalidateQueries({ queryKey: fnbKeys.all });

const after = async <T>(p: Promise<T>): Promise<T> => {
	const res = await p;
	await invalidateFnb();
	return res;
};

// ── Writes ──────────────────────────────────────────────────────────────────

/** `PUT /fnb/settings` — both values, upserted. */
export const putFnbSettings = (body: PutFnbSettingsBody) => after(api.put<FnbSettings>("/fnb/settings", body));

/** `POST /dining-halls` — hall, sessions, diets and buildings in one statement. */
export const createDiningHall = (body: CreateDiningHallBody) => after(api.post<DiningHall>("/dining-halls", body));

/** `PATCH /dining-halls/:id` — `version` from the read; sets sent replace the whole set. */
export const patchDiningHall = (id: string, body: PatchDiningHallBody) => after(api.patch<DiningHall>(`/dining-halls/${id}`, body));

/** `POST /meal-scans` — 201 for OK and REFUSED alike (both logged); unknown / out-of-scope card → 404 CARD_NOT_FOUND. */
export const scanMealPass = async (body: MealScanBody): Promise<MealScanResult> => {
	const res = await api.post<MealScanResult>("/meal-scans", body);
	// The recent-scans list and the plan's served counts move; nothing else does.
	await queryClient.invalidateQueries({ queryKey: ["fnb", "scans"] });
	void queryClient.invalidateQueries({ queryKey: ["fnb", "meal-plan"] });
	return res;
};

/** `POST /takeaway-orders` — a host creates CONFIRMED, a NOC PENDING. */
export const createTakeawayOrder = (body: CreateTakeawayOrderBody) => after(api.post<TakeawayOrderView>("/takeaway-orders", body));

/** `POST /takeaway-orders/:id/status` — one order, with the `version` read. */
export const moveTakeawayOrder = (id: string, body: TakeawayStatusBody) => after(api.post<TakeawayOrderView>(`/takeaway-orders/${id}/status`, body));

/** `POST /takeaway-orders/bulk/status` — ≤ TAKEAWAY_BULK_MAX ids, one guarded UPDATE; the rest come back as `skipped` with a reason. */
export const bulkMoveTakeawayOrders = (body: TakeawayBulkStatusBody) => after(api.post<TakeawayBulkStatusResult>("/takeaway-orders/bulk/status", body));
